import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {envIsPublicCloud} from '../../../config';
import {
  StarredRegistriesDocument,
  useFetchRegistryProjectsPartialQuery,
  useStarredRegistriesQuery,
  useStarRegistryMutation,
  useUnstarRegistryMutation,
} from '../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../../services/analytics';
import {doNotRetryContext, propagateErrorsContext} from '../../../util/errors';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {
  checkRegistryProject,
  getGlobalRegistryType,
  globalRegistryType,
} from './utils';

export const StarredRegistriesContext = createContext({
  starredRegistries: {} as Record<string, globalRegistryType>,
  onStarClick: (
    project: globalRegistryType,
    starred: boolean,
    analyticsLocation: string
  ) => {},
  isLoading: true,
  refetch: () => {},
});

export const StarredRegistriesContextProvider: React.FC = ({children}) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {data: orgData, loading: isOrgDataLoading} =
    useFetchRegistryProjectsPartialQuery({
      variables: {orgName, limit: 1},
      context: propagateErrorsContext(),
      skip: !orgName,
    });
  const orgID = orgData?.organization?.id;

  const {
    data,
    loading: isStarredRegistriesLoading,
    refetch,
  } = useStarredRegistriesQuery({
    variables: {
      organizationId: orgID,
    },
    skip: !orgID,
  });

  const isLoading = isStarredRegistriesLoading || isOrgDataLoading;

  const starredRegistries = useMemo(() => {
    if (isLoading || !data) {
      return {};
    }

    const edges = data.viewer?.starredRegistries?.edges ?? [];
    const registries = edges
      .map(e => e.node)
      .filter(node => !!node)
      .map(node => {
        const {registryName} = checkRegistryProject(node!.name);
        if (!registryName) {
          return null;
        }
        return getGlobalRegistryType(
          node as any,
          registryName,
          node!.entityName,
          orgID ?? '',
          orgName
        );
      })
      .filter((r): r is globalRegistryType => r != null);

    return Object.fromEntries(registries.map(r => [r.id, r]));
  }, [data, isLoading, orgID, orgName]);

  const [starRegistryMutation] = useStarRegistryMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    refetchQueries: [
      {
        query: StarredRegistriesDocument,
        variables: {organizationId: orgID},
      },
    ],
    awaitRefetchQueries: true,
  });

  const [unstarRegistryMutation] = useUnstarRegistryMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),

    refetchQueries: [
      {
        query: StarredRegistriesDocument,
        variables: {organizationId: orgID},
      },
    ],
    awaitRefetchQueries: true,
  });

  const onStarClick = useCallback(
    async (
      registry: globalRegistryType,
      starred: boolean,
      analyticsLocation: string
    ) => {
      if (starred) {
        await starRegistryMutation({
          variables: {
            entityName: registry.entityName,
            registryName: registry.registryName,
          },
        });
        Analytics.track('Registry star added', {
          projectId: registry.id,
          location: analyticsLocation,
        });
      } else {
        await unstarRegistryMutation({
          variables: {
            entityName: registry.entityName,
            registryName: registry.registryName,
          },
        });
        Analytics.track('Registry star removed', {
          projectId: registry.id,
          location: analyticsLocation,
        });
      }
    },
    [starRegistryMutation, unstarRegistryMutation]
  );

  const contextValue = useMemo(
    () => ({
      starredRegistries,
      onStarClick,
      isLoading,
      refetch,
    }),
    [starredRegistries, onStarClick, isLoading, refetch]
  );

  return (
    <StarredRegistriesContext.Provider value={contextValue}>
      {children}
    </StarredRegistriesContext.Provider>
  );
};
